import React from 'react'
import styled from '@emotion/styled'
import { useOutsideClick } from 'rooks'
import { InfiniteData, useMutation, useQuery } from '@tanstack/react-query'
import { dayjs } from '@mobi/utils/date'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { Tooltip } from '@mobi/component-library/Common/V2/Tooltip'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Theme/Common'
import { Icon as LegacyIcon } from '@mobi/component-library/Common/Icon'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { colors as mobiColors } from '@mobi/settings/colors'
import { appHistory } from '@core/Utils/routing/history'
import { AppRoutes } from '@core/AppRoutes'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { QuickbetClosed, SetActiveInvestment } from '@core/Areas/Quickbet/signals'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { createTicketLoadBetDetails } from '@core/Areas/ShareBet/LoadBet/helpers/createTicketLoadBetDetails'
import { loadBet } from '@core/Areas/ShareBet/LoadBet/helpers/loadBet'
import { handleBetSelection } from '@core/Utils/betting/handleBetSelection'
import type { FollowMeSharedBet, ProfileType } from '@core/Areas/FollowMe/types'
import { ProfileAvatar } from '@core/Areas/FollowMe/Components/Common/ProfileAvatar'
import {
  trackFollowMeCopyBet,
  trackFollowMeCopyBetError,
  trackFollowMeViewMyProfile,
  trackFollowMeViewProfile,
} from '@core/Areas/FollowMe/helpers/analytics'
import { FollowButton } from '../FollowButton'
import { BetSelectionDisplay } from './Components/BetSelectionDisplay'
import { followMePrincipleProfileQuery } from '@core/Areas/FollowMe/helpers/queries'
import { useOnboardingTooltips } from '@core/Areas/FollowMe/hooks/useOnboardingTooltips'
import { FOB_TICKET_PLACED_BET_TYPE } from '@core/Areas/ShareBet/LoadBet/constants'

const enum LocalConstants {
  CopyBetButtonClass = 'js-copy-bet__button',
  MyBetLabelClass = 'js-my-bet__label',
  HeaderProfileButtonClass = 'js-header-profile_button',
}

type Props = {
  sharedBet: FollowMeSharedBet
  shouldAutoOpenQuickbet?: boolean
  isMyCopiedBet?: boolean
  isOnProfilePage?: boolean
  isResultedBet?: boolean
}

export const SharedBetCard = React.forwardRef<HTMLDivElement, Props>(
  ({ sharedBet, shouldAutoOpenQuickbet, isMyCopiedBet, isOnProfilePage, isResultedBet }, ref) => {
    const quickbetSubscriptionRef = React.useRef<Rx.IDisposable>()
    const copyBetCountButtonRef = React.useRef<HTMLButtonElement>(null)

    const [isCopyCountTooltipVisible, setCopyCountTooltipVisible] = React.useState(false)
    const [isForcedPendingStatus, setIsForcedPendingStatus] = React.useState(false)

    useOutsideClick(copyBetCountButtonRef, () => setCopyCountTooltipVisible(false))

    React.useEffect(() => {
      return quickbetSubscriptionRef.current?.dispose?.()
    }, [])

    const { data: myProfile } = useQuery(followMePrincipleProfileQuery)

    const { isPending, mutate } = useMutation({
      mutationKey: ['follow-me-shared-bet', sharedBet.postId],
      mutationFn: openSharedBetInQuickbet,
      onSuccess: () =>
        trackFollowMeCopyBet(sharedBet.isFollowing, sharedBet.ticket.productName, sharedBet.ticket),
      onError: err => {
        addToast({
          id: 'follow-me-shared-bet-error',
          title: 'Unable to Copy Share',
          icon: 'error',
          theme: 'dark',
        })
        trackFollowMeCopyBetError(
          err.message ?? '',
          sharedBet.isFollowing,
          sharedBet.ticket.productName
        )
      },
    })

    const handleCopyBetClick = React.useCallback(() => {
      quickbetSubscriptionRef.current = QuickbetClosed.signal$
        .withLatestFrom(quickbetState$, (_, quickbetState) => ({ quickbetState }))
        .take(1)
        .subscribe(x => {
          const quickbetState: QuickbetState = x.quickbetState.toJS()
          if (
            quickbetState.betPlaced &&
            quickbetState.betSourceShareOrigin === sharedBet.postId.toString()
          ) {
            queryClient.setQueriesData<InfiniteData<FollowMeSharedBet[]>>(
              { queryKey: queryKeys.followMeSharedBetsFeedBase() },
              oldData => updateFeedCacheWithIncrementedCopyCount(oldData, sharedBet.postId)
            )
          }
        })
      mutate(sharedBet)
    }, [mutate, sharedBet])

    React.useEffect(() => {
      if (!shouldAutoOpenQuickbet) return
      const timerId = setTimeout(handleCopyBetClick, 1_000)
      return () => clearTimeout(timerId)
    }, [shouldAutoOpenQuickbet, handleCopyBetClick])

    const handleCopyBetCountClick = () => {
      setCopyCountTooltipVisible(curr => !curr)
    }

    const handleForceStatusPendingChange = React.useCallback(() => {
      setIsForcedPendingStatus(true)
    }, [])

    const handleProfileClick = () => {
      if (myProfile?.id === sharedBet.ownerAccountId && !isOnProfilePage) {
        trackFollowMeViewMyProfile()
        appHistory.push(AppRoutes.BetLoopMyProfile)
      } else if (!isOnProfilePage) {
        trackFollowMeViewProfile()
        appHistory.push(`${AppRoutes.BetLoop}/profile/${sharedBet.ownerAccountId}`)
      }
    }

    const isMySharedBet = myProfile?.id === sharedBet.ownerAccountId
    const shouldDisplayCopyBetButton =
      !isMySharedBet &&
      !isMyCopiedBet &&
      sharedBet.ticket.status === 'Pending' &&
      !isForcedPendingStatus &&
      !isResultedBet

    const viewUserProfileTooltipRef = React.useRef<HTMLDivElement>(null)
    const { currentTooltipId, onDismissed } = useOnboardingTooltips('bet-loop-view-user-profile')
    const shouldShowTooltip = currentTooltipId === 'bet-loop-view-user-profile'
    useOutsideClick(viewUserProfileTooltipRef, () => {
      if (shouldShowTooltip) onDismissed('bet-loop-view-user-profile')
    })
    const headerBackgroundColor = getHeaderBackgroundColor(sharedBet.ownerAccountType)
    const sharedAgoText = dayjs(sharedBet.createdOnUtc).fromNow(true) + ' ago'

    return (
      <CardContainerStyled ref={ref}>
        <header style={{ background: headerBackgroundColor }}>
          <button
            type='button'
            onClick={handleProfileClick}
            className={!isOnProfilePage ? LocalConstants.HeaderProfileButtonClass : ''}
          >
            <div ref={viewUserProfileTooltipRef}>
              <ProfileAvatar
                avatar={sharedBet.ownerAccountAvatar}
                color={sharedBet.ownerAccountColor}
              />
            </div>

            <div>
              <span>{sharedBet.ownerAccountDisplayName}</span>
              <span>
                {`${pluralize(sharedBet.ownerAccountFollowerCount, 'Follower')} · Shared ${sharedAgoText.includes('seconds') ? 'just now' : sharedAgoText}`}
              </span>
            </div>
          </button>

          <div>
            {sharedBet.ownerAccountType === 'ExpertTipster' && (
              <LegacyIcon
                type='tabtouchLogoCompact'
                size='2.5em'
                color={mobiColors.brand.primary}
              />
            )}

            {sharedBet.ownerAccountType === 'GiddyUp' && (
              <SvgLazy name='GiddyUpLogo' color='#0051A0' width='6em' />
            )}
          </div>

          {!isMySharedBet ? (
            <FollowButton
              accountId={sharedBet.ownerAccountId}
              accountDisplayName={sharedBet.ownerAccountDisplayName}
              isFollowing={sharedBet.isFollowing}
              isNotificationsEnabled={sharedBet.isNotificationsEnabled}
              location='shared-bet-card'
            />
          ) : null}
        </header>

        {shouldShowTooltip && (
          <Tooltip
            color='blue'
            hasTooltipBeenSeen={() => false}
            shouldHideCrossIcon
            triggerElRef={viewUserProfileTooltipRef}
            tooltipId='bet-loop-view-user-profile'
            offsetYPx={8}
            offsetXPx={40}
            flipElement={true}
            shouldOnlyRenderSingleInstance={true}
            onDismissed={() => onDismissed('bet-loop-view-user-profile')}
          >
            <div style={{ minWidth: '11.2rem' }}>Tap to view stats</div>
            <div>and shared bets</div>
          </Tooltip>
        )}

        <section>
          <BetSelectionDisplay
            onPendingStatusChange={handleForceStatusPendingChange}
            sharedBet={sharedBet}
          />
        </section>

        {shouldDisplayCopyBetButton ? (
          <div className={LocalConstants.CopyBetButtonClass}>
            <Button
              size='sm'
              isFullWidth
              color='positive'
              isLoading={isPending}
              onClick={handleCopyBetClick}
            >
              Copy Bet
            </Button>

            <Button
              size='sm'
              isFullWidth
              color='positive'
              ref={copyBetCountButtonRef}
              onClick={handleCopyBetCountClick}
            >
              <Icon name='DuocolorUsers02' color='white' size='1.6rem' />
              <span> {sharedBet.copyCount}</span>
            </Button>

            <Tooltip
              color='white'
              shouldHideCrossIcon
              hasTooltipBeenSeen={() => false}
              triggerElRef={copyBetCountButtonRef}
              isHidden={!isCopyCountTooltipVisible}
              tooltipId='bet-loop-copy-bet-count-button'
            >
              <div style={{ textAlign: 'center' }}>
                Punters who <br /> copied this bet
              </div>
            </Tooltip>
          </div>
        ) : (
          <div className={LocalConstants.MyBetLabelClass}>
            <Icon name='DuocolorUsers02' size='1.6rem' />
            <span>
              {pluralize(sharedBet.copyCount, 'Punter')}{' '}
              {isMyCopiedBet || !isMySharedBet ? 'copied this bet' : 'copied your bet'}
            </span>
          </div>
        )}
      </CardContainerStyled>
    )
  }
)

SharedBetCard.displayName = 'SharedBetCard'

// =============
// Local Helpers
// =============

async function openSharedBetInQuickbet(sharedBet: FollowMeSharedBet) {
  const loadDetails = createTicketLoadBetDetails(sharedBet.ticket)
  const { selections } = await loadBet(loadDetails, {
    location: 'follow-me',
    origin: sharedBet.postId.toString(),
  })

  const investmentType =
    selections.length === 1 && loadDetails[0].betType === FOB_TICKET_PLACED_BET_TYPE
      ? 'place'
      : 'win'

  handleBetSelection({
    betFlow: {
      location: 'Quickbet',
      options: { setUp: () => SetActiveInvestment(investmentType) },
    },
    selections,
  })
}

const pluralize = (count: number, singular: string, plural?: string) => {
  const label = count === 1 ? singular : (plural ?? `${singular}s`)
  return `${count} ${label}`
}

function updateFeedCacheWithIncrementedCopyCount(
  oldData: InfiniteData<FollowMeSharedBet[]> | undefined,
  id: number
): InfiniteData<FollowMeSharedBet[]> | undefined {
  if (!oldData) return oldData
  return {
    ...oldData,
    pages: oldData.pages.map(page =>
      page.some(bet => bet.postId === id)
        ? page.map(bet => (bet.postId === id ? { ...bet, copyCount: bet.copyCount + 1 } : bet))
        : page
    ),
  }
}

function getHeaderBackgroundColor(type?: ProfileType) {
  switch (type) {
    case 'ExpertTipster':
      return 'linear-gradient(90deg, #E9CDFF 0%, #fff 100%)'
    case 'GiddyUp':
      return 'linear-gradient(90deg, #BDDEFF 0%, #FFF 100%)'
    default:
      return colors.white
  }
}

// ======
// Styles
// ======

const CardContainerStyled = styled.article({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
  backgroundColor: colors.white,
  boxShadow: shadow.sm,
  borderRadius: radius.lgx2,
  border: `1px solid ${colors.neutral[200]}`,
  fontFamily: font.family.primary,
  padding: `0 0 ${spacing.sm} 0`,

  '> header': {
    display: 'flex',
    justifyContent: 'space-between',
    borderRadius: `${radius.lgx2} ${radius.lgx2} 0 0`,
    padding: `${spacing.smx1} ${spacing.sm} ${spacing.smx1} ${spacing.sm}`,

    '> button:first-of-type': {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.smx1,
      textAlign: 'left',
      background: 0,
      border: 0,
      padding: 0,
      opacity: 1,
      transition: 'opacity 0.25s ease',
      WebkitTapHighlightColor: 'transparent',
      touchAction: 'manipulation',

      [`&.${LocalConstants.HeaderProfileButtonClass}`]: {
        '&:active, &:hover': { opacity: 0.5 },
        cursor: 'pointer',
      },

      '> div': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',

        '> span:first-of-type': {
          fontSize: font.size.lg.fontSize,
          fontWeight: font.weight.medium,
          lineHeight: font.size.lg.lineHeight,
          color: colors.black,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },

        '> span:last-of-type': {
          fontSize: font.size.md.fontSize,
          fontWeight: font.weight.regular,
          lineHeight: font.size.md.lineHeight,
          color: colors.neutral[800],
        },
      },
    },

    '> div:first-of-type': {
      display: 'flex',
      alignItems: 'center',

      '> div:first-of-type': {
        marginRight: '1rem',
      },
    },
  },

  '> section, > div': {
    padding: `0 ${spacing.sm} 0 ${spacing.sm}`,
  },

  [`.${LocalConstants.CopyBetButtonClass}`]: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,

    'button:active': { transform: 'scale(1)' },

    '> button:first-of-type': {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRight: `1px solid ${colors.success[600]}`,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },

    '> button:last-of-type': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '0.2rem',
      flexBasis: '8rem',
      borderLeft: `1px solid ${colors.celery[500]}`,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },

  [`.${LocalConstants.MyBetLabelClass}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.smx1,
    fontSize: font.size.lg.fontSize,
    lineHeight: font.size.lg.lineHeight,
    fontWeight: font.weight.medium,
    color: colors.neutral[900],
  },
})
